import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Data } from '../model/data';
import { LocationHierarchy } from '@/model/locations';
import { environment } from '@/environments/environment';
import { OfferWithImage } from '@/model/offer-with-image';
@Injectable({
  providedIn: 'root',
})
export class DataService {
  private dataUrl = './assets/data.json';
  private locationsUrl = `${environment.api}/locations/hierarchy`;
  constructor(private http: HttpClient) {}

  getData(): Observable<Data> {
    return this.http.get<Data>(this.dataUrl);
  }

  getLocations(): Observable<LocationHierarchy> {
    return this.http.get<LocationHierarchy>(this.locationsUrl);
  }
}

// This function is required as per decicision we only set the selected states, districts and locations
// lists and not the whole hierarchy in order to avoid setting a ton of rows in the backend.
// This means that we need to reconstruct the hierarchy based on the selected states, districts and locations
// lists during runtime.
export function getHierarchyForOffer(
  locations: LocationHierarchy,
  offer: OfferWithImage
): LocationHierarchy {
  // If no offer or no locations, return original hierarchy
  if (!offer || !locations) {
    return locations;
  }

  // Create sets of selected IDs for easy lookup - convert to strings for safe comparison
  const selectedStateIds = new Set(
    offer.bundeslaender?.map((state) => state.id.toString()) || []
  );
  const selectedDistrictIds = new Set(
    offer.bezirke?.map((district) => district.id.toString()) || []
  );
  const selectedLocationIds = new Set(
    offer.orte?.map((location) => location.id.toString()) || []
  );

  // Find the district that contains a location by ID
  function findDistrictForLocationId(locationId: string | number) {
    const locationIdStr = locationId.toString();

    for (const state of locations) {
      for (const district of state.bezirke) {
        if (district.orte.some((loc) => loc.id.toString() === locationIdStr)) {
          return district;
        }
      }
    }
    return null;
  }

  // Find the state that contains a location by ID
  function findStateForLocationId(locationId: string | number) {
    const locationIdStr = locationId.toString();

    for (const state of locations) {
      for (const district of state.bezirke) {
        if (district.orte.some((loc) => loc.id.toString() === locationIdStr)) {
          return state;
        }
      }
    }
    return null;
  }

  function getStateForDistrict(districtId: string | number) {
    // Convert districtId to string for safe comparison
    const districtIdStr = districtId.toString();

    for (const state of locations) {
      if (state.bezirke.some((d) => d.id.toString() === districtIdStr)) {
        return state;
      }
    }
    return null;
  }

  // Create a deep copy of the original hierarchy to avoid mutations
  const reconstructedHierarchy: LocationHierarchy = locations.map((state) => ({
    ...state,
    selected: false,
    bezirke: state.bezirke.map((district) => ({
      ...district,
      selected: selectedDistrictIds.has(district.id.toString()),
      orte: district.orte.map((location) => ({
        ...location,
        selected: selectedLocationIds.has(location.id.toString()),
      })),
    })),
  }));

  // If allAustria is true, we don't need specific selections
  if (offer.allAustria) {
    return reconstructedHierarchy;
  }

  // Mark selected states directly
  for (const state of reconstructedHierarchy) {
    if (selectedStateIds.has(state.id.toString())) {
      state.selected = true;
    }
  }

  // Mark selected districts and their parent states
  for (const district of offer.bezirke || []) {
    const districtId = district.id;

    // Find the state this district belongs to
    const state = getStateForDistrict(districtId);

    if (state) {
      // Find the matching state in our reconstructed hierarchy
      const reconstructedState = reconstructedHierarchy.find(
        (s) => s.id.toString() === state.id.toString()
      );
      if (reconstructedState) {
        // Mark the state as selected
        reconstructedState.selected = true;

        // Find and mark the district as selected
        const reconstructedDistrict = reconstructedState.bezirke.find(
          (d) => d.id.toString() === districtId.toString()
        );
        if (reconstructedDistrict) {
          reconstructedDistrict.selected = true;
        }
      }
    }
  }

  // Mark selected locations and their parent districts and states
  for (const location of offer.orte || []) {
    const locationId = location.id;

    // Find the state and district this location belongs to
    const state = findStateForLocationId(locationId);
    const district = findDistrictForLocationId(locationId);

    if (state && district) {
      // Find the matching state in our reconstructed hierarchy
      const reconstructedState = reconstructedHierarchy.find(
        (s) => s.id.toString() === state.id.toString()
      );

      if (reconstructedState) {
        // Mark the state as selected
        reconstructedState.selected = true;

        // Find and mark the district as selected
        const reconstructedDistrict = reconstructedState.bezirke.find(
          (d) => d.id.toString() === district.id.toString()
        );

        if (reconstructedDistrict) {
          reconstructedDistrict.selected = true;

          // Find and mark all locations with matching ID
          reconstructedDistrict.orte.forEach((locationInHierarchy) => {
            if (locationInHierarchy.id.toString() === locationId.toString()) {
              locationInHierarchy.selected = true;
            }
          });
        }
      }
    }
  }

  // Double check all locations to make sure we didn't miss any
  reconstructedHierarchy.forEach((state) => {
    state.bezirke.forEach((district) => {
      district.orte.forEach((location) => {
        if (selectedLocationIds.has(location.id.toString())) {
          location.selected = true;
          district.selected = true;
          state.selected = true;
        }
      });
    });
  });

  return reconstructedHierarchy;
}
